import { CONSENT_CATEGORIES } from './constants';

// Everything this app knows about Usercentrics lives in this file. The rest of
// the codebase talks to the store, so swapping the CMP means rewriting only
// this adapter.
//
// State is always read from UC_UI.getServicesBaseInfo(). The events below are
// used purely as a trigger to re-read - their payload shape differs between
// Browser UI versions, and relying on it would break on an upgrade we do not
// control.
const CMP_EVENTS = ['UC_UI_INITIALIZED', 'UC_UI_CMP_EVENT', 'ucEvent'];

const getUcUi = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  // An ad blocker removes the script entirely, so this is undefined far more
  // often than it looks.
  return window.UC_UI || null;
};

export const isCmpPresent = () => {
  try {
    return Boolean(getUcUi()?.isInitialized?.());
  } catch (error) {
    return false;
  }
};

// Returns null when the CMP has not answered yet - deliberately different from
// "answered, everything denied", which is an empty-ish object.
//
// Verified against the live CMP on 2026-09-17: entries carry id, name,
// categorySlug, consent.status, isEssential, isHidden and subServices. Known
// gap: subServices are ignored. Every service in this configuration has an
// empty subServices array today, so a service with partially granted
// sub-services would currently be read from its parent status alone.
export const readConsentState = () => {
  try {
    const ucUi = getUcUi();

    if (
      !ucUi?.isInitialized?.() ||
      typeof ucUi.getServicesBaseInfo !== 'function'
    ) {
      return null;
    }

    const baseInfo = ucUi.getServicesBaseInfo();

    if (!Array.isArray(baseInfo)) {
      return null;
    }

    const services = {};
    const categories = {};

    baseInfo.forEach((service) => {
      const granted = Boolean(service?.consent?.status);

      // Indexed by id and by name: ids are stable and are what the code gates
      // on, names are what a human reads in the panel and in a bug report.
      if (service?.id) {
        services[service.id] = granted;
      }

      if (service?.name) {
        services[service.name] = granted;
      }

      const categorySlug = service?.categorySlug;

      // Hidden services have no toggle of their own in the CMP, so folding
      // them into the category verdict would let an invisible entry veto a
      // category the visitor actually accepted.
      if (categorySlug && !service?.isHidden) {
        // A category counts as granted only when every service in it is.
        categories[categorySlug] =
          categories[categorySlug] === undefined
            ? granted
            : categories[categorySlug] && granted;
      }
    });

    categories[CONSENT_CATEGORIES.ESSENTIAL] = true;

    return { services, categories };
  } catch (error) {
    return null;
  }
};

export const subscribeToCmpEvents = (handler) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  CMP_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handler);
  });

  return () => {
    CMP_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handler);
    });
  };
};

export const acceptService = (serviceId) => {
  try {
    const ucUi = getUcUi();

    if (!serviceId || typeof ucUi?.acceptService !== 'function') {
      return Promise.resolve(false);
    }

    return Promise.resolve(ucUi.acceptService(serviceId))
      .then(() => true)
      .catch(() => false);
  } catch (error) {
    return Promise.resolve(false);
  }
};

export const showSecondLayer = () => {
  try {
    const ucUi = getUcUi();

    if (typeof ucUi?.showSecondLayer !== 'function') {
      return false;
    }

    ucUi.showSecondLayer();

    return true;
  } catch (error) {
    return false;
  }
};
